/**
 * Shared date-range preset computation for dashboard period filters.
 * Pure date math, no UI - see components/common/PeriodSelector.tsx for the control.
 */
import { format } from 'date-fns'

export type DateRangePresetKey = '7d' | '30d' | 'current_month' | 'current_fy' | 'last_12_months' | 'custom'

export interface DateRangePreset {
  key: DateRangePresetKey
  label: string
}

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { key: '7d', label: 'Last 7 Days' },
  { key: '30d', label: 'Last 30 Days' },
  { key: 'current_month', label: 'Current Month' },
  { key: 'current_fy', label: 'Current Financial Year' },
  { key: 'last_12_months', label: 'Last 12 Months' },
  { key: 'custom', label: 'Custom Range' },
]

export const DEFAULT_DATE_RANGE_PRESET: DateRangePresetKey = '30d'

const toISODate = (d: Date) => format(d, 'yyyy-MM-dd')

/** Indian financial year starts April 1. Mirrors backend/app/utils/gst_utils.py's invoice-numbering FY rule. */
const currentFinancialYearStart = (today: Date): Date => {
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1
  return new Date(year, 3, 1) // April = month index 3
}

/**
 * Resolve a preset key to concrete ISO date strings. 'custom' has no computed
 * range - the caller must supply dateFrom/dateTo directly instead of calling this
 * (passing 'custom' falls through to today-to-today, see default case below).
 */
export function computePresetRange(key: DateRangePresetKey): { dateFrom: string; dateTo: string } {
  const today = new Date()
  const dateTo = toISODate(today)

  switch (key) {
    case '7d': {
      const from = new Date(today)
      from.setDate(from.getDate() - 7)
      return { dateFrom: toISODate(from), dateTo }
    }
    case '30d': {
      const from = new Date(today)
      from.setDate(from.getDate() - 30)
      return { dateFrom: toISODate(from), dateTo }
    }
    case 'current_month': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1)
      return { dateFrom: toISODate(from), dateTo }
    }
    case 'current_fy': {
      return { dateFrom: toISODate(currentFinancialYearStart(today)), dateTo }
    }
    case 'last_12_months': {
      const from = new Date(today)
      from.setDate(from.getDate() - 365)
      return { dateFrom: toISODate(from), dateTo }
    }
    default:
      return { dateFrom: toISODate(today), dateTo }
  }
}
